import { Component, computed, ElementRef, inject, Input, OnDestroy, Renderer2, signal, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject, timer } from 'rxjs';
import { map, mergeMap, shareReplay, takeUntil, tap } from 'rxjs/operators';

import { BaseCommonClientRequest, ClientRequestIdentityHelper, RemoveClientRequestFiles } from '@aiwyn/common/client-requests';

import { ClientRequestDocumentUploadModel, ClientRequestItemStatus, ClientRequestStatus } from '@aiwyn/api';
import { ConfigService } from '@aiwyn/config';
import {
  compareStringsIgnoreCase,
  DATA_SELECTOR_INJECTION_TOKEN,
  ignoreNullish,
  mapToOrResult,
  UploadFileJob,
  UploadFilesComponent,
  UploadFileStatus,
} from '@aiwyn/shared';
import { LaunchDarklyService } from 'src/app/config/services/launch-darkly.service';
import { LaunchDarklyFlag, LaunchDarklyState } from 'src/app/config/state';

import { FilePreviewService } from '@aiwyn/services';
import { toSignal } from '@angular/core/rxjs-interop';
import { FilesLinkModalComponent, FilesLinkModalData } from '../../../../../components';
import { ClientRequestDocumentFileDisplay, ClientRequestDocumentFileDisplayStatus } from '../../../../../models';
import {
  ClientRequestsState,
  ClientRequestSubmitFileData,
  StageFilesForClientRequest,
  SubmitFilesForClientRequest,
} from '../../../../../state';
import { buildDocumentPreviewList } from './document-upload-tab.utils';

enum FileFilterOption {
  ALL = 'all',
  UNCATEGORIZED = 'uncategorized',
  FLAGGED = 'flagged',
}

interface DocumentSectionToggleEvent {
  sectionId: number;
  type: 'open' | 'close';
}

const FILE_STATUS_ORDINALS: Record<string, number> = {
  [ClientRequestDocumentFileDisplayStatus.UPLOADING]: 0,
  [ClientRequestDocumentFileDisplayStatus.CATEGORIZING]: 1,
  [ClientRequestDocumentFileDisplayStatus.ERROR]: 2,
  [ClientRequestDocumentFileDisplayStatus.CATEGORIZATION_TIMEOUT]: 2,
};

@Component({
  selector: 'awn-client-request-document-upload-tab',
  templateUrl: './document-upload-tab.component.html',
  styleUrls: ['./document-upload-tab.component.scss', '../../../../../styles/client-requests.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'document-upload-tab',
    },
  ],
  standalone: false,
})
export class ClientRequestDocumentUploadTabComponent implements OnDestroy {
  showOnlyUncategorized = signal<boolean>(false);
  showOnlyFlagged = signal<boolean>(false);
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  isFileFlaggingEnabled = toSignal(this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_FILE_FLAGGING));

  selectedFilter = signal<FileFilterOption>(FileFilterOption.ALL);

  // Expose enum to template
  readonly FileFilterOption = FileFilterOption;

  constructor(
    private readonly _configService: ConfigService,
    private readonly _dialog: MatDialog,
    private readonly _filePreviewService: FilePreviewService,
    private readonly _host: ElementRef<ClientRequestDocumentUploadTabComponent & HTMLElement>,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly _renderer: Renderer2,
    private readonly _store: Store,
  ) {
    this.manageSectionToggleTransition$.pipe(takeUntil(this._unsubscribe$)).subscribe();
    this.acceptedFileTypes = this._configService.clientRequestFileTypeWhitelist;
  }

  @Input() instance!: BaseCommonClientRequest<ClientRequestDocumentUploadModel>;

  private readonly _unsubscribe$ = new Subject<void>();
  acceptedFileTypes: string[] = [];

  isEditable$ = defer(() => this.instance.status$).pipe(
    ignoreNullish(),
    map((status) => [ClientRequestStatus.IN_PROGRESS, ClientRequestStatus.SENT].includes(status)),
    shareReplay(1),
  );

  jobId$ = defer(() => this.instance.groupIdentity$).pipe(
    map((identity) => ClientRequestIdentityHelper.parseGroupIdentity(identity)),
    map((parsedIdentity) => parsedIdentity.id),
  );

  sectionToggleEvent$ = new BehaviorSubject<DocumentSectionToggleEvent>(undefined);
  manageSectionToggleTransition$ = this.sectionToggleEvent$.pipe(
    ignoreNullish(),
    mergeMap(({ sectionId, type }) => {
      const sectionChildren = Array.from(this._host.nativeElement.getElementsByClassName(`section-${sectionId}`));

      if (type === 'open') {
        this.sectionCollapsedMap[sectionId] = false;

        return timer(250).pipe(tap((_) => sectionChildren.forEach((child: HTMLElement) => this._renderer.removeStyle(child, 'height'))));
      }

      sectionChildren.forEach((child: HTMLElement) => this._renderer.setStyle(child, 'height', `${child.offsetHeight}px`));
      this.sectionCollapsedMap[sectionId] = true;

      return of(undefined);
    }),
    takeUntil(this._unsubscribe$),
  );

  sectionCollapsedMap: Record<string, boolean> = {};

  @ViewChild(UploadFilesComponent) uploadFilesComponent: UploadFilesComponent;

  stagedFileDisplays$: Observable<ClientRequestDocumentFileDisplay[]> = this._store.select(ClientRequestsState.getStagedFileDisplays).pipe(
    map((fileDisplays) =>
      fileDisplays.sort((a, b) => {
        const statusDiff = FILE_STATUS_ORDINALS[a.status] - FILE_STATUS_ORDINALS[b.status];
        return statusDiff === 0 ? compareStringsIgnoreCase(a.name, b.name) : statusDiff;
      }),
    ),
    shareReplay(1),
  );

  uncategorizedFileDisplays$ = this._store.select(ClientRequestsState.getUncategorizedFileDisplays).pipe(
    map((fileDisplays) => fileDisplays.sort((a, b) => compareStringsIgnoreCase(a.name, b.name))),
    shareReplay(1),
  );

  categorizedFileDisplays$ = this._store.select(ClientRequestsState.getCategorizedFileDisplaysWithFlagging).pipe(
    map((fileDisplays) => fileDisplays.sort((a, b) => compareStringsIgnoreCase(a.name, b.name))),
    shareReplay(1),
  );

  categorizedAndFlaggedFileDisplays$ = this.categorizedFileDisplays$.pipe(
    map((fileDisplays) => fileDisplays.filter((fileDisplay) => fileDisplay.isFlagged)),
    shareReplay(1),
  );

  // Computed counts for radio button labels
  uncategorizedCount = toSignal(this.uncategorizedFileDisplays$.pipe(map((files) => files.length)), { initialValue: 0 });
  categorizedCount = toSignal(this.categorizedFileDisplays$.pipe(map((files) => files.length)), { initialValue: 0 });
  flaggedCount = toSignal(this.categorizedAndFlaggedFileDisplays$.pipe(map((files) => files.length)), { initialValue: 0 });

  allFilesCount = computed(() => this.uncategorizedCount() + this.categorizedCount());

  shouldShowUncategorizedFiles = computed(() => {
    if (!this.isFileFlaggingEnabled()) {
      return true; // Always show when flagging disabled
    }
    return this.selectedFilter() === FileFilterOption.ALL || this.selectedFilter() === FileFilterOption.UNCATEGORIZED;
  });

  shouldShowCategorizedFiles = computed(() => {
    if (!this.isFileFlaggingEnabled()) {
      return !this.showOnlyUncategorized(); // Use old logic when flagging disabled
    }
    return this.selectedFilter() === FileFilterOption.ALL || this.selectedFilter() === FileFilterOption.FLAGGED;
  });

  shouldShowOnlyFlaggedCategorized = computed(() => {
    return this.isFileFlaggingEnabled() && this.selectedFilter() === FileFilterOption.FLAGGED;
  });

  haveFilesBeenUploaded$: Observable<boolean> = combineLatest([
    // are any fully uploaded files present
    this._store.select(ClientRequestsState.getSelectedClientRequestDetails).pipe(
      ignoreNullish(),
      map((details) => details.files.length > 0),
    ),
    // are any uploading/categorizing files present
    this._store.select(ClientRequestsState.getStagedFileDisplays).pipe(map((files) => files.length > 0)),
  ]).pipe(mapToOrResult());

  fileToRequestItemsMapping$ = this._store.select(ClientRequestsState.getFileToRequestItemsWithStatusMapping).pipe(shareReplay(1));

  toggleUncategorizedFilter(event: MatCheckboxChange): void {
    this.showOnlyUncategorized.set(event.checked);
  }

  toggleFlaggedFilter(event: MatCheckboxChange): void {
    this.showOnlyFlagged.set(event.checked);
  }

  onFilterChange(filterOption: FileFilterOption): void {
    this.selectedFilter.set(filterOption);
  }

  onUploadFilesStart(files: UploadFileJob[]): void {
    // stage files (will be uploaded to our DB once DMS upload succeeds)
    this._store.dispatch(new StageFilesForClientRequest(files.map((f) => f.jobId)));
  }

  onUploadFilesComplete(files: UploadFileJob[]): void {
    const successfulUploads = files.filter((file) => file.status === UploadFileStatus.LOADED);

    // submit files
    const marshaledFiles: ClientRequestSubmitFileData[] = successfulUploads.map((file) => ({
      jobId: file.jobId,
      documentIds: [] as number[],
    }));

    this._store.dispatch(new SubmitFilesForClientRequest(marshaledFiles));
  }

  previewFile(siblings: ClientRequestDocumentFileDisplay[] | null, file: ClientRequestDocumentFileDisplay): void {
    const { files, initialIndex } = buildDocumentPreviewList(siblings, file);
    this._filePreviewService.previewMany(files, initialIndex);
  }

  canDeleteFile(file: ClientRequestDocumentFileDisplay): boolean {
    const fileToItemsMapping = this._store.selectSnapshot(ClientRequestsState.getFileToRequestItemsWithStatusMapping);
    const linkedItems = fileToItemsMapping.get(file.id);

    // If no linked items, allow deletion
    if (!linkedItems || linkedItems.length === 0) {
      return true;
    }

    const isInReviewEditingEnabled = this._store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_IN_REVIEW_ITEMS_EDITING));

    if (isInReviewEditingEnabled) {
      // New behavior: Prevent deletion if ANY linked item is DONE
      return !linkedItems.some(item =>
        item.status === ClientRequestItemStatus.DONE
      );
    } else {
      // Original behavior: Prevent deletion if ANY linked item is DONE or IN_REVIEW
      return !linkedItems.some(item =>
        item.status === ClientRequestItemStatus.DONE ||
        item.status === ClientRequestItemStatus.IN_REVIEW
      );
    }
  }

  getFileDeleteTooltip(file: ClientRequestDocumentFileDisplay): string {
    const fileToItemsMapping = this._store.selectSnapshot(ClientRequestsState.getFileToRequestItemsWithStatusMapping);
    const linkedItems = fileToItemsMapping.get(file.id);

    // Only show tooltip if file cannot be deleted
    if (!linkedItems || linkedItems.length === 0) {
      return '';
    }

    const isInReviewEditingEnabled = this._store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_IN_REVIEW_ITEMS_EDITING));

    // Find items that prevent deletion
    const blockingStatuses = linkedItems
      .filter(item => {
        if (isInReviewEditingEnabled) {
          return item.status === ClientRequestItemStatus.DONE;
        } else {
          return item.status === ClientRequestItemStatus.DONE || item.status === ClientRequestItemStatus.IN_REVIEW;
        }
      })
      .map(item => {
        switch (item.status) {
          case ClientRequestItemStatus.IN_REVIEW:
            return 'In Review';
          case ClientRequestItemStatus.DONE:
            return 'Accepted';
        }
      });

    if (blockingStatuses.length === 0) {
      return '';
    }

    const uniqueStatuses = Array.from(new Set(blockingStatuses));

    if (uniqueStatuses.length === 1) {
      return `This file cannot be deleted because it is linked to a request item that is ${uniqueStatuses[0]}`;
    } else {
      return 'This file cannot be deleted because it is linked to request items that are under review or already accepted';
    }
  }

  deleteFile(file: ClientRequestDocumentFileDisplay): void {
    if (!this.canDeleteFile(file)) {
      return;
    }

    const selectedClientRequestId = this._store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
    this._store.dispatch(new RemoveClientRequestFiles(selectedClientRequestId, [file.id]));
  }

  linkToRequests(file: ClientRequestDocumentFileDisplay): void {
    this._dialog.open<FilesLinkModalComponent, FilesLinkModalData>(FilesLinkModalComponent, {
      data: {
        clientRequestId: this._store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId),
        file,
        instance: this.instance,
        notApplicableItemIds: new Set(this._store.selectSnapshot(ClientRequestsState.getNotApplicableItemsForSelectedRequest)),
      },
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
  protected readonly ClientRequestDocumentFileDisplayStatus = ClientRequestDocumentFileDisplayStatus;
}
